import { useState, useEffect, useCallback, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';

import { ascending, descending } from 'd3-array'
import Moment from 'moment-timezone';
import queryString from 'query-string';

import { useGlobalAuthStore } from '@Stores/global/auth';
import LossesStore from 'modules/report/stores/losses'
import surveysStore from 'modules/report/stores/surveys';
import appStore from 'stores/app'
import { formatDate } from 'utils'
import history from 'utils/history';

const keys = [
  'site',
  'organization',
  'publisher',
  'allocation',
  'pathUserProfile',
  'path',
  'page',
  'customField',
  'form',
  'post',
  'integration',
  'post_target',
  'rev__integrationId',
];

function useRows(initialState, onChange) {
  const [reference, setReference] = useState(initialState)
  const [changed, setChanged] = useState(null)

  useEffect(() => {
    onChange && onChange(reference)
  }, [changed])

  reference.remove = useCallback(row => {
    const idx = reference.findIndex(r => r == row);
    reference.splice(idx, 1)
    setChanged({})
  }, [reference])

  reference.add = useCallback((pos, rows) => {
    reference.splice(pos, 0, ...rows)
    setChanged({})
  }, [reference])

  reference.set = state => {
    setReference(state)
    setChanged({})
  }

  return reference
}

export function useDynamicOptions(groups = [], enabled = true) {
  const [loadedOptions, setLoadedOptions] = useState(null)

  const { staticGroups, dynamicGroups } = useMemo(() => {
    const staticGroups = []
    const dynamicGroups = []

    groups.forEach(group => {
      if (typeof group?.options === 'function') {
        dynamicGroups.push(group)
      } else {
        staticGroups.push(group)
      }
    })

    return { staticGroups, dynamicGroups }
  }, [groups])

  const hasDynamicGroup = !!dynamicGroups.length

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!hasDynamicGroup) {
      return;
    }

    let abort = false

    const loadDynamicOptions = async () => {
      const groups = await Promise.all(dynamicGroups.map(async ({ group, options: loadOptions, ...groupProps }) => {
        try {
          const options = await loadOptions() || []
          return options.map(option => ({ group, ...groupProps, ...option }))
        } catch (error) {
          return []
        }
      }))

      if (!abort) {
        setLoadedOptions(groups.flat())
      }
    }

    loadDynamicOptions()

    return () => {
      abort = true
    }
  }, [enabled, hasDynamicGroup, dynamicGroups])

  const dynamicGroupsState = useMemo(() => dynamicGroups.map(({ group }) => ({
    group,
    isLoading: !loadedOptions,
  })), [dynamicGroups, loadedOptions])

  return useMemo(() => [
    [...staticGroups, ...loadedOptions || []],
    dynamicGroupsState,
  ], [staticGroups, loadedOptions, dynamicGroupsState])
}

const isDayBlocked = day => Moment.tz(appStore.timezone).isBefore(day, 'day');

const dateRangeFormatter = (date, mask) => [
  Moment.utc(date).tz(appStore.timezone).startOf(mask),
  Moment.utc(date).tz(appStore.timezone).endOf(mask),
]

const { sorted } = {
  comparator: order => field => (a, b) => order(field(a), field(b)),
  get [-1]() {
    return this.comparator(ascending)
  },
  get [1]() {
    return this.comparator(descending)
  },
  get sorted() {
    return (array, field, order) => {
      order = this[order]
      return array.sort(order(field))
    }
  },
}

function getUrlLink(row, isSuperAdmin) {
  if (row.key === 'post' || row.key === 'post_target') {
    const { post: { objectsMapping } } = LossesStore
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;
    if (objectsMapping[row.value]) return `/cms/${objectsMapping[row.value].content_type}/${row.value}`;

    return '/cms'
  }

  if (row.key === 'site' || row.key === 'organization') {
    if (!isSuperAdmin) return `/reports`
    return `/admin/${row.key}s/${row.value}`
  }

  if (row.key === 'path' || row.key === 'pathUserProfile' || row.key === 'page') {
    return `/path/${row.key === 'pathUserProfile' ? 'dashboard/user_profile' : `${row.key}s`}/${row.value}`;
  }

  if (row.key === 'customField') {
    const { customField: { objectsMapping } } = surveysStore;
    if (objectsMapping[row.value]) return `/path/fields/${objectsMapping[row.value].id}`;

    return '/path/fields';
  }

  if (row.key === 'rev__integrationId' || row.key === 'integration') return `/settings/integrations/${row.value}`;

  if (row.key === 'form') return `/offer/forms/${row.value}/fields`;

  return `/offer/${`${row.key}s`}/${row.value}`;
}

function weekOfMonthFormatter(dateValue) {
  const date = Moment(dateValue).tz(appStore.timezone);
  const endOfWeek = date.clone().endOf('week');
  const sameMonth = date.month() === endOfWeek.month();

  return `${`${date.startOf('week').format('MMM Do')} to ${endOfWeek.format(sameMonth ? 'Do' : 'MMM Do')}`}`;
}

function LinkName({ row }) {
  const { auth: { isSuperAdmin } } = useGlobalAuthStore()
  const link = useMemo(() => getUrlLink(row, isSuperAdmin), [row, isSuperAdmin])

  return <>
    <Link to={link}>
      {[row.value === '' ? '--' : !row.name ? row.value : `[#${row.value}]`, row.name].filter(Boolean).join(' ')}
    </Link>
  </>
}

const formatters = {
  Loadable: ({ isLoading, children }) => <span>{children} {isLoading && <small><i>Loading</i></small>}</span>,

  money(decimalScale = 2) {
    return cell => {
      if (cell === null || cell === undefined) {
        return '--'
      }

      if (cell && cell.isComparison) {
        return this.comparison(cell, this.money(decimalScale), this.colorfulPercent)
      }

      return <NumberFormat
        value={Number(cell).toFixed(decimalScale)}
        displayType="text"
        thousandSeparator=","
        decimalSeparator="."
        prefix={'$'}
        decimalScale={decimalScale}
        fixedDecimalScale={true}
      />
    }
  },

  number(cell) {
    if (cell === null || cell === undefined) {
      return '--'
    }

    if (cell && cell.isComparison) {
      return this.comparison(cell, this.number)
    }

    return <NumberFormat
      value={cell.toFixed(2)}
      displayType="text"
      thousandSeparator=","
      decimalSeparator="."
      prefix={''}
      decimalScale={0}
      fixedDecimalScale={true}
    />
  },

  percent(cell, total = 1) {
    if (cell === null || cell === undefined) {
      return '--'
    }

    if (cell && cell.isComparison) {
      return this.comparison(cell, this.percent)
    }

    return <NumberFormat
      value={(cell / total) * 100}
      displayType="text"
      decimalSeparator="."
      suffix={'%'}
      decimalScale={2}
      fixedDecimalScale={true}
    />
  },

  colorfulPercent(valueA, valueB) {
    if ([null, undefined].includes(valueA) || [null, undefined].includes(valueB)) {
      return '--'
    }

    let value = (valueA - valueB) / Math.abs(valueB)
    if (valueA && !valueB) value = 1
    if (!valueA && valueB) value = -1
    if (valueA === valueB) value = 0
    return <>
      <NumberFormat
        value={value * 100}
        displayType="text"
        decimalSeparator="."
        suffix={'%'}
        decimalScale={1}
        fixedDecimalScale={true}
        className={value === 0 ? '' : value > 0 ? 'twy-text-success' : 'twy-text-danger'}
      />
    </>
  },

  defaultComparison(value) {
    return <NumberFormat
      value={value.toFixed(2)}
      displayType="text"
      thousandSeparator=","
      decimalSeparator="."
      prefix={''}
      decimalScale={0}
      fixedDecimalScale={true}
    />
  },

  parentPercent(formatter, dataField = 'total') {
    return (cell, row) => {
      if (cell && cell.isComparison) {
        return this.comparison(cell, formatter, this.colorfulPercent)
      }

      const parentPercent = cell != null && row.parent && row.parent[dataField] && <div className='text-sm italic'>
        {formatters.percent(row[dataField] / row.parent[dataField])}
      </div>
      return <>
        {formatter(cell)}{parentPercent || null}
      </>
    }
  },

  comparison(cell, metricFormatter, diferenceFormatter) {
    const { valueA, valueB } = cell
    metricFormatter = metricFormatter || this.defaultComparison
    diferenceFormatter = diferenceFormatter || this.colorfulPercent
    return <>
      <div>
        {metricFormatter(valueA)}
      </div>
      <div>
        {metricFormatter(valueB)}
      </div>
      <div>
        {diferenceFormatter(valueA, valueB)}
      </div>
    </>
  },

  name(_, row) {
    if (row.formattedName) {
      return row.formattedName
    }

    if (row.key === 'experimentVersion') {
      return row.formattedName = row.value === 0 ? 'N/A' : `version ${row.value}`
    }

    if (row.key === 'pathContainerSlot') {
      return row.formattedName = row.value === 0 ? 'N/A' : `Slot ${row.name + 1}`
    }

    if (row.key === 'day' || row.key === 'day_session') {
      return row.formattedName = formatDate(parseInt(row.value))
    }

    if (row.key === 'hour' || row.key === 'hour_session') {
      return row.formattedName = formatDate(parseInt(row.value), 'MM/DD/YYYY [at] HH:mm')
    }

    if (row.key === 'month' || row.key === 'month_session') {
      return row.formattedName = formatDate(parseInt(row.value), 'MM/YYYY')
    }

    if (row.key === 'week' || row.key === 'week_session') {
      return row.formattedName = weekOfMonthFormatter(parseInt(row.value))
    }

    if (row.key === 'year') {
      return row.formattedName = formatDate(parseInt(row.value), 'YYYY')
    }

    if (row.key === 'day_unittime') {
      return row.formattedName = formatDate(parseInt(row.value), '[Day] DD')
    }

    if (row.key === 'hour_unittime') {
      return row.formattedName = formatDate(parseInt(row.value), 'HH [h]')
    }

    if (row.key === 'month_unittime') {
      return row.formattedName = formatDate(parseInt(row.value), 'MMMM')
    }

    if (row.key === 'weekday_unittime') {
      return row.formattedName = formatDate(parseInt(row.value), 'dddd')
    }

    if (row.value != 0 && keys.includes(row.key)) {
      return <LinkName row={row} />
    }

    return `${row.value === '' ? '--' : row.value}${row.name ? ` - ${row.name}` : ''}`
  },

  groupsText(group) {
    return group.replace(/__/g, '. ').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, ($, $1, $2) => `${$1} ${$2}`)
  },

  fallback(cell) {
    return cell >= 0 ? cell : '--'
  },

  colorize: (cell, value) => <span className={value < 0 ? 'text-red-400 dark:text-red-300' : 'text-green-400 dark:text-green-300'}>{cell}</span>,

  drilldown(onClick, dataField) {
    return (cell, row) => {
      if (cell === null || cell === undefined) {
        return '--'
      }

      if (cell && cell.isComparison) {
        return this.comparison(cell)
      }

      return <>
        <span className="hover:underline cursor-pointer">
          <NumberFormat
            onClick={event => onClick(event, dataField, row)}
            value={cell.toFixed(2)}
            displayType="text"
            thousandSeparator=","
            decimalSeparator="."
            prefix={''}
            decimalScale={0}
            fixedDecimalScale={true}
          />
        </span>
        {cell !== null && row.parent && row.parent[dataField] ? <div className='text-sm italic'>{formatters.percent(row[dataField] / row.parent[dataField])}</div> : null}
      </>
    }
  },

  openNewTab(pathname, params) {
    const handleClick = () => {
      try {
        const query = btoa(JSON.stringify(params))
        const search = queryString.stringify({ configure: query })
        window.open(history.createHref({ pathname, search }))
      } catch (error) {
        console.error(error)
      }
    }

    return <a href='#'
      className="text-secondary"
      onClick={() => handleClick()}
    >
      {params.value}
    </a>
  },

  drilldownDB(onClick, { params, label, pathname }) {
    return <span className="twy-text-base-accent cursor-pointer hover:underline" onClick={event => onClick(event, params, pathname)}>
      {label}
    </span>
  },

  newTabDrilldownDB(event, params, pathname) {
    try {
      event.stopPropagation()

      const state = JSON.stringify(params)
      const configure = btoa(state)
      const search = queryString.stringify({ configure })

      const href = history.createHref({
        pathname: `${history.location.pathname}${pathname}`,
        search,
      })
      window.open(href)
    } catch (error) {
      console.log(error)
    }
  },

  seconds(cell) {
    if (cell === null || cell === undefined) {
      return '--'
    }

    if (cell && cell.isComparison) {
      return this.comparison(cell, this.seconds)
    }

    return <NumberFormat
      value={(cell / 1000)}
      displayType="text"
      thousandSeparator=","
      decimalSeparator="."
      prefix={''}
      suffix=' sec'
      decimalScale={4}
    />
  },
}

export {
  formatters,
  useRows,
  sorted,
  isDayBlocked,
  weekOfMonthFormatter,
  dateRangeFormatter,
}
